import { useCallback, useEffect, useState } from 'react';

// Owns the boolean visibility flag for the per-tree-forest-attributes layer's
// native-resolution drill-down. The contract — pinned by tests — is:
//
//   1. Default = inactive.
//   2. `toggle()` flips the flag.
//   3. `deactivate()` is idempotent and forces inactive.
//   4. **Auto-deactivation**: any change to the dependency tuple (mill code,
//      drive time, primary layer) snaps the flag back to `false`. The cached
//      GeoJSON is shed-specific; leaving the view "on" while panning to a
//      different shed would render off-target crowns until the new fetch
//      resolved.
//
// This is split out of DetailView so the auto-deactivation contract can be
// exercised without standing up the full router / Leaflet / S3 stack.
export const useNativeViewState = (
  millId: string | null | undefined,
  driveTime: number,
  primaryLayer: string | null,
): {
  active: boolean;
  toggle: () => void;
  /** Idempotent — forces active. Used by scenario steps that flag
      `autoActivateNativeView` so advancing into the step kicks off the
      cinematic fly without requiring a manual button click. */
  activate: () => void;
  deactivate: () => void;
} => {
  const [active, setActive] = useState(false);

  useEffect(() => {
    setActive(false);
  }, [millId, driveTime, primaryLayer]);

  const toggle = useCallback(() => setActive((v) => !v), []);
  const activate = useCallback(() => setActive(true), []);
  const deactivate = useCallback(() => setActive(false), []);

  return { active, toggle, activate, deactivate };
};
